/**
 * Espejo de `research/nombres.py` para avisar antes de enviar el formulario.
 *
 * El backend es la autoridad —estas mismas reglas corren allí y rechazan
 * igual—, pero repetirlas aquí ahorra el viaje y, sobre todo, permite
 * explicar el problema mientras se escribe en vez de después.
 *
 * Si se cambian las reglas, hay que cambiarlas en los dos sitios; el test
 * `tests/test_nombres.py` cubre el lado que manda.
 */

export const LARGO_MINIMO = 3
export const LARGO_PALABRA_SOLA = 6

/** Palabras que nombran la categoría, no al miembro. */
const GENERICAS = new Set(
  `empresa empresas compania compañia companias compañias sociedad sociedades
   corporacion corporaciones grupo holding negocio negocios firma
   mina minas minera mineras faena faenas yacimiento planta plantas proyecto
   proyectos obra obras proveedor proveedores contratista contratistas
   universidad universidades instituto institutos colegio colegios escuela
   escuelas liceo fundacion fundaciones ong onegs
   municipalidad municipalidades municipio gobierno ministerio ministerios
   servicio servicios institucion instituciones organismo organismos
   agencia agencias direccion departamento comision consejo
   comunidad comunidades sindicato sindicatos gremio gremios asociacion
   asociaciones federacion confederacion junta juntas club
   banco bancos super hiper mega centro centros
   cliente clientes usuario usuarios vecino vecinos gente personas
   norte sur este oeste chile chilena chileno nacional regional local`
    .split(/\s+/)
    .filter(Boolean),
)

export interface RevisionNombre {
  /** El texto ya normalizado, que es lo que se envía. */
  limpio: string
  /** `invalido` no se puede enviar; `dudoso` se puede confirmar. */
  nivel: 'ok' | 'dudoso' | 'invalido'
  mensaje: string
}

function sinTildes(texto: string): string {
  return texto.normalize('NFD').replace(/\p{Mn}/gu, '')
}

export function revisarNombre(nombre: string): RevisionNombre {
  const limpio = nombre.trim().replace(/\s+/g, ' ')

  if (!limpio) {
    return { limpio, nivel: 'invalido', mensaje: '' }
  }

  if (limpio.length < LARGO_MINIMO) {
    return {
      limpio,
      nivel: 'invalido',
      mensaje: `«${limpio}» es demasiado corto para identificar una entidad. Escribe al menos ${LARGO_MINIMO} caracteres, o mejor el nombre completo.`,
    }
  }

  if (!/\p{L}/u.test(limpio)) {
    return {
      limpio,
      nivel: 'invalido',
      mensaje:
        'El nombre tiene que contener letras. Escribe el nombre de la empresa, faena, proyecto o institución.',
    }
  }

  const palabras = limpio.split(' ')
  if (palabras.length === 1) {
    const sola = sinTildes(palabras[0])
      .toLowerCase()
      .replace(/^[.,;:_-]+|[.,;:_-]+$/g, '')
    if (GENERICAS.has(sola)) {
      return {
        limpio,
        nivel: 'dudoso',
        mensaje: `«${limpio}» nombra una categoría, no una entidad concreta, y el modelo acabará eligiendo una por su cuenta. Añade el nombre propio, o confirma para buscarlo igual.`,
      }
    }
    if (sola.length < LARGO_PALABRA_SOLA) {
      return {
        limpio,
        nivel: 'dudoso',
        mensaje: `«${limpio}» es una sola palabra muy corta. Si es una sigla (SQM, BHP), confirma para continuar; si no, escribe el nombre completo para no gastar una corrida en el resultado equivocado.`,
      }
    }
  }

  return { limpio, nivel: 'ok', mensaje: '' }
}
